import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import {
  CREATED_AT_KEY_PARAM,
  DESC,
  DIRECTION_KEY_PARAM,
  ENDPOINT_QUOTES,
  FILTERS_CODE,
  ORDER_KEY_PARAM,
  PAGE_KEY_PARAM,
  QUOTE_TYPES,
  ROUTE_EDIT,
  ROUTE_QUOTES,
  STATUS_KEY_PARAM,
  TERM_KEY_PARAM,
  TYPE_KEY_PARAM,
} from '@/common/constants';
import scrollToAnchor from '@/common/helpers/scrollToAnchor';
import usePageParams from '@/common/hooks/usePageParams';
import useQuotesCreateDraft from '@/ebat/hooks/quotes/draft/useQuotesCreateDraft';
import useQuotesCreateInstaller from '@/ebat/hooks/quotes/installer/useQuotesCreateInstaller';
import useQuotes from '@/ebat/hooks/quotes/useQuotes';

import { useAuth } from '../AuthContext';
import {
  QuotesContextProviderDefaultProps,
  QuotesContextProviderPropTypes,
} from './QuotesContextProvider.proptypes';

/**
 * Data parameters
 * name: url query and state name
 * defaultValue: default value on state initialization
 * encode: state to url query
 * decode : url query to state
 */
const paramsConfig = {
  [ORDER_KEY_PARAM]: {
    name: ORDER_KEY_PARAM,
    defaultValue: CREATED_AT_KEY_PARAM,
    encode: (value) => value,
    decode: (value) => value,
  },
  [DIRECTION_KEY_PARAM]: {
    name: DIRECTION_KEY_PARAM,
    defaultValue: DESC,
    encode: (value) => value,
    decode: (value) => value,
  },
  [PAGE_KEY_PARAM]: {
    name: PAGE_KEY_PARAM,
    defaultValue: 1,
    encode: (value) => value,
    decode: (value) => value,
  },
  [TYPE_KEY_PARAM]: {
    name: TYPE_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [TERM_KEY_PARAM]: {
    name: TERM_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [STATUS_KEY_PARAM]: {
    name: STATUS_KEY_PARAM,
    defaultValue: [],
    encode: (value) => value,
    decode: (value) => value,
  },
};

const QuotesContext = createContext(null);

const QuotesContextProvider = ({ children }) => {
  const { user, zergRush } = useAuth();
  const history = useHistory();

  const {
    params,
    setParams,
    getParams,
    apiParams,
    hasSearchParamsDiff,
    getQueryParams,
  } = usePageParams(paramsConfig);

  const queryKey = [ENDPOINT_QUOTES, apiParams];

  const {
    data,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useQuotes(queryKey, hasSearchParamsDiff);

  /**
   * Sort
   * @param {string} order
   * @param {string} direction
   * @type {function(*, *): void}
   */
  const handleSort = (order, direction) => {
    setParams({
      [ORDER_KEY_PARAM]: order,
      [DIRECTION_KEY_PARAM]: direction,
      [PAGE_KEY_PARAM]: 1,
    });
  };

  /**
   * Pagination
   * @param {number} page
   * @type {function(*): void}
   */
  const handlePaginate = (page) => {
    scrollToAnchor('#main-body');

    setParams({
      [PAGE_KEY_PARAM]: page,
    });
  };

  /**
   * Filter status
   * @param {array} status
   * @type {function(*): void}
   */
  const handleFilter = (status) => {
    setParams({
      [STATUS_KEY_PARAM]: status,
      [PAGE_KEY_PARAM]: 1,
    });
  };

  /**
   * Set type to pageParam
   * @param {string} type
   * @type {function(*): void}
   */
  const handleType = (type) => {
    setParams({
      [TYPE_KEY_PARAM]: type !== '' ? type : null,
      [PAGE_KEY_PARAM]: 1,
    });
  };

  /**
   * Search
   * @param {string} term
   * @type {function(*): void}
   */
  const handleSearch = (term) => {
    setParams({
      [TERM_KEY_PARAM]: term !== '' ? term : null,
      [PAGE_KEY_PARAM]: 1,
    });
  };

  const {
    isDisabled: isDisabledUseQuotesCreateInstaller,
    onCreateQuoteInstaller,
  } = useQuotesCreateInstaller({ isLoading });
  const {
    isDisabled: isDisabledUseQuotesCreateDraft,
    onCreateQuoteDraft,
  } = useQuotesCreateDraft({ isLoading });

  // Extract statuses from filters
  const filtersStatus = useMemo(() => {
    let statusesSet = data?.filters.data.filter(
      (item) => item.code === FILTERS_CODE.STATUS,
    );

    if (statusesSet && statusesSet.length > 0) {
      statusesSet = statusesSet.shift();

      return statusesSet.values;
    }

    return null;
  }, [data]);

  // Extract types from filters
  const filtersTypes = useMemo(() => {
    let typesSet = data?.filters.data.filter(
      (item) => item.code === FILTERS_CODE.TYPE,
    );

    if (typesSet && typesSet.length > 0) {
      typesSet = typesSet.shift();

      return typesSet.values;
    }

    return null;
  }, [data]);

  /**
   * Return disabled state
   * @type {boolean}
   */
  const isDisabled = useMemo(() => {
    const loading =
      isFetching ||
      isDisabledUseQuotesCreateDraft.loading ||
      isDisabledUseQuotesCreateInstaller.loading;

    return {
      createInstaller: loading || !user?.functionality_accesses?.quotes_create,
      createQuote:
        loading || zergRush || !user?.functionality_accesses?.quotes_quote,
      disabled:
        isDisabledUseQuotesCreateDraft.disabled ||
        isDisabledUseQuotesCreateInstaller.disabled,
      loading,
    };
  }, [
    isDisabledUseQuotesCreateDraft,
    isDisabledUseQuotesCreateInstaller,
    isFetching,
    user,
    zergRush,
  ]);

  /**
   * Redirect Callback on success draft created
   * @param {string} id
   */
  const redirectToQuote = useCallback(
    (quote) =>
      history.push(
        `${ROUTE_QUOTES}/${quote.id}${
          quote.id.substring(0, 1) === QUOTE_TYPES.letter.installer
            ? ROUTE_EDIT
            : ''
        }`,
      ),
    [history],
  );

  /**
   * Create draft and redirect on it with the given ID
   * @type {function(): void}
   */
  const handleCreateDraft = useCallback(() => {
    onCreateQuoteDraft({ onSuccess: redirectToQuote });
  }, [redirectToQuote, onCreateQuoteDraft]);

  /**
   * Create installer and redirect on it with the given ID
   * @type {function(): void}
   */
  const handleCreateInstaller = useCallback(() => {
    onCreateQuoteInstaller({ onSuccess: redirectToQuote });
  }, [onCreateQuoteInstaller, redirectToQuote]);

  return (
    <QuotesContext.Provider
      value={{
        data,
        filtersStatus,
        filtersTypes,
        getParams,
        getQueryParams,
        handleCreateDraft,
        handleCreateInstaller,
        handleFilter,
        handlePaginate,
        handleSearch,
        handleSort,
        handleType,
        isDisabled,
        isError,
        isSuccess,
        params,
        refetch,
        setParams,
      }}
    >
      {children}
    </QuotesContext.Provider>
  );
};

QuotesContextProvider.defaultProps = {
  ...QuotesContextProviderDefaultProps,
};

QuotesContextProvider.propTypes = {
  ...QuotesContextProviderPropTypes,
};

export const useQuotesContext = () => useContext(QuotesContext);

export default QuotesContextProvider;
